<script lang="ts">
  import { onMount } from 'svelte';
  import {
    createCommunityApiKey,
    fetchCommunityApiKeys,
    revokeCommunityApiKey,
    type CommunityApiKeySummary
  } from '../../lib/convexApi';
  import Notice from '../ui/Notice.svelte';

  export let communityId: string;
  export let isOwner = false;

  let keys: CommunityApiKeySummary[] = [];
  let loading = true;
  let error: string | null = null;

  let newName = '';
  let creating = false;
  let mintedSecret: string | null = null;
  let copied = false;
  let confirmRevokePrefix: string | null = null;
  let busyPrefix: string | null = null;

  async function refresh() {
    loading = true;
    error = null;
    try {
      keys = await fetchCommunityApiKeys(communityId);
    } catch (err) {
      error = err instanceof Error ? err.message : 'Could not load API keys.';
    } finally {
      loading = false;
    }
  }

  onMount(() => {
    void refresh();
  });

  async function createKey() {
    if (!newName.trim()) return;
    creating = true;
    error = null;
    mintedSecret = null;
    try {
      const result = await createCommunityApiKey(communityId, newName.trim());
      mintedSecret = result.secret;
      newName = '';
      await refresh();
    } catch (err) {
      error = err instanceof Error ? err.message : 'Could not create the key.';
    } finally {
      creating = false;
    }
  }

  async function copySecret() {
    if (!mintedSecret) return;
    try {
      await navigator.clipboard.writeText(mintedSecret);
      copied = true;
      setTimeout(() => (copied = false), 1500);
    } catch {
      error = 'Could not copy — select and copy the key manually.';
    }
  }

  async function revoke(prefix: string) {
    confirmRevokePrefix = null;
    busyPrefix = prefix;
    error = null;
    try {
      await revokeCommunityApiKey(communityId, prefix);
      await refresh();
    } catch (err) {
      error = err instanceof Error ? err.message : 'Could not revoke the key.';
    } finally {
      busyPrefix = null;
    }
  }

  function formatDate(ts: number | null): string {
    if (!ts) return 'never';
    return new Date(ts).toLocaleString(undefined, {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
</script>

<section class="apikeys surface surface--sunken" aria-label="API keys">
  <h4>Agent API keys</h4>
  <p class="apikeys__hint">
    Keys let agents and scripts manage this community's events over the HTTP API —
    guest lists, check-ins, invites, applications. Scoped to this community only.
  </p>

  {#if error}
    <Notice variant="error">{error}</Notice>
  {/if}

  {#if mintedSecret}
    <div class="apikeys__secret surface">
      <p class="apikeys__secret-label">
        Copy this key now — it is shown only once and cannot be recovered.
      </p>
      <code class="apikeys__secret-value">{mintedSecret}</code>
      <button type="button" class="accent" on:click={copySecret}>
        {copied ? 'Copied!' : 'Copy key'}
      </button>
    </div>
  {/if}

  {#if isOwner}
    <form class="apikeys__create" on:submit|preventDefault={createKey}>
      <input type="text" placeholder="Key name, e.g. door-agent" bind:value={newName} />
      <button type="submit" class="secondary" disabled={creating || !newName.trim()}>
        {creating ? 'Creating…' : 'Create key'}
      </button>
    </form>
  {:else}
    <p class="apikeys__hint">Only the community owner can create or revoke keys.</p>
  {/if}

  {#if loading && keys.length === 0}
    <p class="apikeys__hint">Loading…</p>
  {:else if keys.length === 0}
    <p class="apikeys__hint">No keys yet.</p>
  {:else}
    <ul>
      {#each keys as key (key.prefix)}
        <li class="apikeys__row" class:apikeys__row--revoked={key.status === 'revoked'}>
          <div class="apikeys__info">
            <span class="apikeys__name">{key.name} <code>{key.prefix}…</code></span>
            <span class="apikeys__meta">
              {key.status} · acts as {key.actsAs} · last used {formatDate(key.lastUsedAt)}
            </span>
          </div>
          {#if isOwner && key.status === 'active'}
            {#if confirmRevokePrefix === key.prefix}
              <div class="apikeys__confirm">
                <button
                  type="button"
                  class="apikeys__danger"
                  disabled={busyPrefix === key.prefix}
                  on:click={() => revoke(key.prefix)}
                >
                  Confirm revoke
                </button>
                <button type="button" class="ghost" on:click={() => (confirmRevokePrefix = null)}>
                  Keep
                </button>
              </div>
            {:else}
              <button type="button" class="ghost" on:click={() => (confirmRevokePrefix = key.prefix)}>
                Revoke
              </button>
            {/if}
          {/if}
        </li>
      {/each}
    </ul>
  {/if}
</section>

<style>
  .apikeys {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    padding: var(--space-4);
    border-radius: var(--radius-lg);
  }

  .apikeys h4 {
    margin: 0;
    font-size: var(--font-size-sm);
    color: var(--color-text-muted);
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  .apikeys__hint {
    margin: 0;
    font-size: var(--font-size-xs);
    color: var(--color-text-muted);
    max-width: 60ch;
  }

  .apikeys__secret {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    padding: var(--space-3);
    border-radius: var(--radius-md);
    border: 1px solid rgb(var(--accent-rgb) / 0.5);
  }

  .apikeys__secret-label {
    margin: 0;
    font-size: var(--font-size-xs);
    color: var(--color-text-amber);
    font-weight: 600;
  }

  .apikeys__secret-value {
    font-size: var(--font-size-xs);
    word-break: break-all;
    padding: var(--space-2);
    background: var(--color-surface-900);
    border-radius: var(--radius-sm);
  }

  .apikeys__secret button {
    align-self: flex-start;
  }

  .apikeys__create {
    display: flex;
    gap: var(--space-2);
    flex-wrap: wrap;
  }

  .apikeys__create input {
    flex: 1 1 220px;
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-sm);
    border: var(--border-subtle);
    background: var(--color-surface-900);
    color: var(--color-text-primary);
    font-size: var(--font-size-sm);
  }

  .apikeys ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .apikeys__row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-md);
    border: var(--border-subtle);
    background: var(--color-surface-850);
    flex-wrap: wrap;
  }

  .apikeys__row--revoked {
    opacity: 0.55;
  }

  .apikeys__info {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .apikeys__name {
    font-weight: 600;
    font-size: var(--font-size-sm);
  }

  .apikeys__name code {
    font-size: var(--font-size-2xs);
    color: var(--color-text-muted);
    margin-left: var(--space-2);
  }

  .apikeys__meta {
    font-size: var(--font-size-xs);
    color: var(--color-text-muted);
  }

  .apikeys__confirm {
    display: flex;
    gap: var(--space-2);
  }

  .apikeys__danger {
    border: 1px solid rgba(255, 107, 107, 0.5);
    border-radius: var(--radius-pill);
    background: rgba(255, 107, 107, 0.1);
    color: #ff8f8f;
    cursor: pointer;
    font-size: var(--font-size-xs);
    padding: var(--space-1) var(--space-3);
  }
</style>
